import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';

/**
 * Shared modal for confirming line arrival (mark as IN)
 * Used by both ReceivingDashboard and APIReceiving pages
 */
const ArrivalConfirmationModal = ({
  open,
  onClose,
  line,
  order,
  onMarkAsIn,
  onMarkAsInAndPrint,
  isUpdating = false,
  isPrinting = false
}) => {
  if (!line) return null;

  // Calculate total quantity from sizes if not directly available
  const totalQty = line.totalQuantity || 
    Object.values(line.sizes || {}).reduce((a, b) => a + b, 0);

  // Get customer name from order or line
  const customerName = order?.customerName || line.customerName || 'Unknown Customer';

  // Get garment info - handle different field names
  const garmentStyle = line.garmentId || line.garmentStyle || 'N/A';
  const garmentColor = line.garmentColor || 'N/A';

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          background: 'var(--al-surface)',
          color: 'white',
          minWidth: '400px',
          borderRadius: '12px'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        Mark Line as Received?
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ 
          background: 'rgba(var(--al-primary-rgb), 0.1)', 
          p: 2, 
          borderRadius: 1,
          mb: 2
        }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            {line.designName || 'Unnamed Design'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {customerName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {garmentStyle} • {garmentColor}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
            Qty: {totalQty} pieces
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 2, 
        gap: 1,
        borderTop: '1px solid rgba(255,255,255,0.1)',
        flexDirection: 'column',
        alignItems: 'stretch'
      }}>
        <Button 
          onClick={onMarkAsInAndPrint}
          variant="contained"
          disabled={isPrinting || isUpdating}
          startIcon={<LocalPrintshopIcon />}
          sx={{ 
            backgroundColor: 'var(--al-primary)',
            color: '#222',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#7FD17F' }
          }}
        >
          {isPrinting ? 'Printing...' : 'Mark as IN & Print Sticker'}
        </Button>
        <Button 
          onClick={onMarkAsIn}
          variant="outlined"
          disabled={isUpdating}
          sx={{ 
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'white',
            '&:hover': { 
              borderColor: 'var(--al-primary)',
              backgroundColor: 'rgba(var(--al-primary-rgb), 0.1)'
            }
          }}
        >
          Mark as IN (No Sticker)
        </Button>
        <Button 
          onClick={onClose}
          sx={{ color: 'rgba(255,255,255,0.5)' }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArrivalConfirmationModal;
